import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, Download, Music2, Filter, ShoppingCart, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useCart, useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryNames = {
  drum_kit: "Drum Kits",
  sample_pack: "Sample Packs",
  loop_kit: "Loop Kits",
  preset_pack: "Preset Packs",
  one_shot: "One Shots"
};

const SoundKitsPage = () => {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchKits = async () => {
      setLoading(true);
      try {
        const params = {};
        if (searchQuery) params.search = searchQuery;
        if (selectedCategory !== "all") params.category = selectedCategory;
        if (sortBy) params.sort_by = sortBy;
        const res = await axios.get(`${API}/soundkits/`, { params });
        setKits(res.data);
      } catch (e) {
        console.error("Error fetching sound kits:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchKits();
  }, [searchQuery, selectedCategory, sortBy]);

  const handleAddToCart = async (kit) => {
    await addToCart({
      item_type: "soundkit",
      item_id: kit.kit_id,
      quantity: 1
    });
  };

  return (
    <div className="min-h-screen py-10 px-6" data-testid="soundkits-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50 mb-4">
            <Music2 className="w-3 h-3 mr-1" />Producer Tools
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Sound Kits & Samples
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Premium drum kits, sample packs, loop kits, and one-shots to fuel your production.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search kits..."
              className="bg-black/50 border-white/10 pl-10 h-12"
              data-testid="soundkit-search"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="bg-black/50 border-white/10 w-full sm:w-48 h-12" data-testid="soundkit-category-filter">
              <Filter className="w-4 h-4 mr-2 text-zinc-500" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(categoryNames).map(([key, name]) => (
                <SelectItem key={key} value={key}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="bg-black/50 border-white/10 w-full sm:w-44 h-12" data-testid="soundkit-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-square bg-zinc-900/50 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : kits.length === 0 ? (
          <div className="text-center py-20">
            <Music2 className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No sound kits found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {kits.map((kit, index) => (
              <motion.div
                key={kit.kit_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                data-testid={`soundkit-card-${kit.kit_id}`}
              >
                <div className="group glass overflow-hidden card-hover">
                  <div className="relative aspect-square overflow-hidden bg-zinc-800">
                    <img
                      src={kit.image_url || "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=400"}
                      alt={kit.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <Badge className="absolute top-3 left-3 bg-black/60 text-orange-400 border-orange-500/50 text-xs uppercase">
                      {categoryNames[kit.category] || kit.category}
                    </Badge>
                    {kit.on_sale && (
                      <Badge className="absolute top-3 right-3 bg-red-500 text-white border-0 text-xs font-bold">
                        <Zap className="w-3 h-3 mr-1" />{kit.sale_discount}% OFF
                      </Badge>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium truncate mb-1">{kit.name}</h3>
                    <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{kit.description}</p>
                    <div className="flex items-center gap-2 text-xs text-zinc-600 mb-3">
                      <span>{kit.sample_count} samples</span>
                      <span>|</span>
                      <span>{kit.format}</span>
                      <span>|</span>
                      <span><Download className="w-3 h-3 inline" /> {kit.downloads || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      {kit.on_sale ? (
                        <div className="flex items-center gap-2">
                          <span className="text-red-400 font-mono text-lg font-bold">${kit.price.toFixed(2)}</span>
                          <span className="text-zinc-600 font-mono text-sm line-through">${kit.original_price?.toFixed(2)}</span>
                        </div>
                      ) : (
                        <span className="text-orange-400 font-mono text-lg font-bold">${kit.price.toFixed(2)}</span>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(kit)}
                        className="bg-orange-500/20 text-orange-400 hover:bg-orange-500 hover:text-black"
                        data-testid={`add-kit-${kit.kit_id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />Add
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SoundKitsPage;
